"use client";

import "./css/font-awesome.min.css";
import "./css/style.css";
import backgroundGif2 from "./img/gifWhite.gif";
import logo from "./img/Medco_full_Logo.png";
import featureImage from "./img/DemoImage.jpg";
import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { ChevronLeft, ChevronRight } from "lucide-react";
import BookDemoModal from "./BookDemoModal";

function Home() {
  const auth = getAuth();
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [videoUrls, setVideoUrls] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    const storage = getStorage();
    const videoPaths = [
      "LandingPageVideos/video 5.mp4",
      "LandingPageVideos/video 2.mp4",
      "LandingPageVideos/video 7.mp4",
      "LandingPageVideos/video 4.mp4",
      "LandingPageVideos/video 1_1.mp4",
      "LandingPageVideos/video 6_1.mp4",
      "LandingPageVideos/video 3.mp4",
    ];

    const fetchVideoUrls = async () => {
      try {
        const urls = await Promise.all(
          videoPaths.map(async (path) => {
            const videoRef = ref(storage, path);
            return await getDownloadURL(videoRef);
          })
        );
        setVideoUrls(urls);
      } catch (error) {
        console.error("Error fetching video URLs:", error);
      } finally {
        setLoadingVideos(false);
      }
    };

    fetchVideoUrls();
  }, []);

  useEffect(() => {
    videoRefs.current = videoRefs.current.slice(0, videoUrls.length);
  }, [videoUrls]);

  const handlePrev = () => {
    // Pause current video
    if (videoRefs.current[activeIndex]) {
      videoRefs.current[activeIndex].pause();
    }

    setActiveIndex((prev) => (prev === 0 ? videoUrls.length - 1 : prev - 1));
  };

  const handleNext = () => {
    // Pause current video
    if (videoRefs.current[activeIndex]) {
      videoRefs.current[activeIndex].pause();
    }

    setActiveIndex((prev) => (prev === videoUrls.length - 1 ? 0 : prev + 1));
  };

  // Get the index for the video to the left of the active video
  const getLeftIndex = () => {
    return activeIndex === 0 ? videoUrls.length - 1 : activeIndex - 1;
  };

  // Get the index for the video to the right of the active video
  const getRightIndex = () => {
    return activeIndex === videoUrls.length - 1 ? 0 : activeIndex + 1;
  };

  // Determine the position class for each video
  const getPositionClass = (index) => {
    if (index === activeIndex) return "active";
    if (index === getLeftIndex()) return "left";
    if (index === getRightIndex()) return "right";
    return "hidden";
  };

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const handleBookDemo = () => {
    setIsModalOpen(true);
  };

  const handleCall = () => {
    window.location.href = "tel:+2010016128869";
  };

  const handleEmail = () => {
    window.location.href = "mailto:hazemtgs@me.com";
  };

  return (
    <div className="landing-page">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="background-container">
          <img
            src={backgroundGif2 || "/placeholder.svg"}
            alt="Medical background"
            className="background-gif"
          />
        </div>

        <div className="corner-buttons">
          <button className="secondary-btn corner-btn left-btn" onClick={handleBookDemo}>
            <i className="fa fa-calendar"></i> Book Demo
          </button>
          <button
            className="primary-btn corner-btn right-btn"
            onClick={() => navigate("/authentication/sign-in")}
          >
            <i className="fa fa-sign-in"></i> Login
          </button>
        </div>

        <div className="centered-content">
          <h1>Digitalize Your Clinic with Med-CO</h1>
          <p>Streamlining Care, Simplifying Management</p>
        </div>
      </div>
      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Enhanced Video Carousel Section */}
      <section className="videos-section">
        <div className="videos-container">
          <section className="video-carousel-section">
            <div className="video-carousel-container">
              <h2 className="section-title">Watch Our Features in Action</h2>

              {loadingVideos ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Loading videos...</p>
                </div>
              ) : (
                <div className="enhanced-carousel-wrapper">
                  <button
                    className="carousel-arrow carousel-arrow-left"
                    onClick={handlePrev}
                    aria-label="Previous video"
                  >
                    <ChevronLeft className="arrow-icon" />
                  </button>

                  <div className="enhanced-carousel-viewport">
                    <div className="enhanced-carousel-container">
                      {videoUrls.map((url, index) => (
                        <div
                          key={index}
                          className={`enhanced-carousel-item ${getPositionClass(index)}`}
                        >
                          <div className="enhanced-video-wrapper">
                            <video
                              controls
                              ref={(el) => (videoRefs.current[index] = el)}
                              className="enhanced-video"
                              onPlay={() => {
                                // Pause all other videos when one starts playing
                                videoRefs.current.forEach((videoRef, i) => {
                                  if (i !== index && videoRef) {
                                    videoRef.pause();
                                  }
                                });
                              }}
                            >
                              <source src={url} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                            <div className="video-overlay">
                              <div className="play-button-overlay">
                                <div className="play-icon-triangle"></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    className="carousel-arrow carousel-arrow-right"
                    onClick={handleNext}
                    aria-label="Next video"
                  >
                    <ChevronRight className="arrow-icon" />
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="value-props">
        <div className="container">
          <h2 className="section-title">Why Choose Med-CO?</h2>
          <div className="props-wrapper">
            <div className="props-grid">
              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-clock-o"></i>
                </div>
                <h3>Save Multiple Hours Weekly</h3>
                <p>Digitalize scheduling, billing, and record-keeping to focus on patient care</p>
              </div>

              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-line-chart"></i>
                </div>
                <h3>Increase Revenue</h3>
                <p>Reduce no-shows and streamline billing to improve cash flow</p>
              </div>

              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-shield"></i>
                </div>
                <h3>Safety</h3>
                <p>High-grade security to protect patient data</p>
              </div>

              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-users"></i>
                </div>
                <h3>Patient Portal</h3>
                <p>Empower patients with 24/7 access to their health records</p>
              </div>

              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-file-text"></i>
                </div>
                <h3>Automated Documentation</h3>
                <p>Reduce paperwork with smart templates and reports</p>
              </div>

              <div className="prop-card">
                <div className="prop-icon">
                  <i className="fa fa-bar-chart"></i>
                </div>
                <h3>Real-time Analytics</h3>
                <p>Track clinic performance with customizable dashboards</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="feature-highlights">
        <div className="container">
          <div className="feature-row">
            <div className="feature-text">
              <h2>Comprehensive Patient Management</h2>
              <ul>
                <li>
                  <i className="fa fa-check"></i> Integrated electronic health records
                </li>
                <li>
                  <i className="fa fa-check"></i> Customizable patient data
                </li>
                <li>
                  <i className="fa fa-check"></i> Appointment reminders
                </li>
              </ul>
            </div>
            <div className="feature-image">
              <img src={featureImage || "/placeholder.svg"} alt="Med-CO Dashboard" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container">
          <h2>Ready to modernize your practice?</h2>
          <p>Join multiple clinics using Med-CO to deliver better care</p>
          <div className="cta-buttons">
            <button className="secondary-btn large" onClick={handleCall}>
              <i className="fa fa-phone"></i> Get Started Today
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="main-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-about">
              <img src={logo || "/placeholder.svg"} alt="Med-CO" className="footer-logo" />
              <p>The complete clinic management platform,</p>
              <p>for modern healthcare providers.</p>
            </div>
            <div className="footer-contact">
              <h4>Contact Us</h4>
              <p>
                <i className="fa fa-envelope"></i>
                <a
                  href="#"
                  onClick={handleEmail}
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    textDecorationThickness: "1px",
                    textUnderlineOffset: "3px",
                    transition: "text-decoration 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  ceo@thedevhouse.io
                </a>
              </p>

              <p>
                <i className="fa fa-phone"></i>
                <a
                  href="#"
                  onClick={handleCall}
                  style={{
                    color: "inherit",
                    textDecoration: "none",
                    textDecorationThickness: "1px",
                    textUnderlineOffset: "3px",
                    transition: "text-decoration 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  +20 100 16128869
                </a>
              </p>

              <p>
                <i class="fa fa-lock" />
                <Link to="/privacy-policy" style={{ color: "inherit", textDecoration: "none" }}>
                  Privacy Policy
                </Link>
              </p>

              <div className="social-links">
                <a
                  href="https://www.facebook.com/people/MedCo/61575253312860/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="fa fa-facebook"></i>
                </a>
                <a href="https://www.instagram.com/medcoplatform/" target="_blank" rel="noreferrer">
                  <i className="fa fa-instagram"></i>
                </a>
                <a href="https://wa.me/+201001612869" target="_blank" rel="noreferrer">
                  <i className="fa fa-phone" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Med-CO. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
